/**
 * `useFormCacheSelector` — server-state coordinator for the FormCacheSelector
 * component.
 *
 * Implements the mount-time auto-restore flow described in Architecture §9.4:
 *
 *   1. Fetch GetAllByUser via TanStack Query.
 *   2. On success:
 *      a. If any record has lastInUse === true → fetch GetById, run safeParse,
 *         call onSelected(restoredFilter, true).
 *      b. Else → call onSelected(emptyFilter, false).
 *   3. On error → call onSelected(emptyFilter, false) (NFR-FFCS-STB-002).
 *
 * The mount-time call to `onSelected` MUST fire exactly once (FR-FFCS-011).
 * A `useRef` flag guards against duplicate fires when:
 *   - React 18 StrictMode invokes effects twice in development.
 *   - The query refetches because of focus / network reconnect.
 *   - Parent re-renders cause the effect dependency array to re-evaluate.
 *
 * In S013 the hook gains its first real mutation (`handleUpsert`):
 *   - Sets `isMutating` while the request is in-flight.
 *   - On 200: pessimistically updates the local `caches` list using the
 *     server-returned `id`, switches `activeId`, surfaces a success message
 *     that auto-dismisses after 3 s (D-UX-006).
 *   - On 409: routes the error into `saveNameError` (rendered inline in
 *     SaveDialog) — the global error banner stays clear.
 *   - On other errors: routes the error into `errorMessage` (global banner)
 *     and leaves the dialog open.
 *
 * The auto-dismiss timer is cleaned up in a `useEffect` return so toggling
 * `successMessage` mid-life cancels any stale timer and unmounting cancels
 * the pending one (R-001 / no setState-after-unmount warnings).
 *
 * In S014 the hook gains `handleRename` plus the `renameDialogOpen` /
 * `renameError` state used by the RenameDialog subcomponent:
 *   - On 200: list updates in-place (`id` unchanged, only `name` changes), the
 *     dialog closes, success banner fires.
 *   - On 409: `renameError` is set so the dialog renders inline conflict text
 *     and stays open — the user can edit the name and retry.
 *   - On 404 / other errors: dialog closes and the global error banner is set.
 *
 * In S015 the hook gains `handleDelete` plus the `deleteDialogOpen` /
 * `deletingCacheName` state used by the DeleteDialog subcomponent:
 *   - `openDeleteDialog()` snapshots the active cache name (R-001) so the
 *     dialog title cannot drift mid-confirmation.
 *   - On 200: the entry is removed from the local `caches` list. If it was
 *     the active cache, `activeId` resets to null AND `onSelected(emptyFilter,
 *     false)` fires AFTER the local state update (R-002) so the parent form
 *     observes a consistent local state.
 *   - On error: dialog closes and the global error banner shows the message.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { createFormCacheApi, formCacheKeys } from './FormCacheSelector.api';
import { safeParse } from './FormCacheSelector.utils';
import type { FormCacheDTO, FormCacheSelectorProps, HttpClient } from './FormCacheSelector.types';

/** i18n key surfaced in `saveNameError` when the backend responds with 409. */
const NAME_CONFLICT_KEY = 'errors.nameAlreadyExists';

/** Payload for the in-tree toast rendered via createPortal in FormCacheSelector. */
export interface ToastPayload {
  text: string;
  color: 'green' | 'red';
}

export interface UseFormCacheSelectorResult<TFilter> {
  caches: FormCacheDTO[];
  activeId: string | null;
  isLoading: boolean;
  isMutating: boolean;
  /** Save dialog open state — exposed so the root component can render it. */
  saveDialogOpen: boolean;
  /** Setter for `saveDialogOpen` so the root component can open / close it. */
  setSaveDialogOpen: (open: boolean) => void;
  /** i18n key for an inline 409 error inside the save dialog (or null). */
  saveNameError: string | null;
  /** Clears `saveNameError` — called as the user edits the name input. */
  clearSaveNameError: () => void;
  /** Rename dialog open state — exposed so the root component can render it. */
  renameDialogOpen: boolean;
  /** Setter for `renameDialogOpen` so the root component can open / close it. */
  setRenameDialogOpen: (open: boolean) => void;
  /** i18n key for an inline 409 error inside the rename dialog (or null). */
  renameError: string | null;
  /** Clears `renameError` — called as the user edits the rename input. */
  clearRenameError: () => void;
  /** Delete dialog open state — exposed so the root component can render it. */
  deleteDialogOpen: boolean;
  /** Setter for `deleteDialogOpen` so the root component can dismiss it. */
  setDeleteDialogOpen: (open: boolean) => void;
  /**
   * Cache name snapshot taken when the delete dialog was opened (R-001). The
   * dialog renders this verbatim so a concurrent rename can't relabel the
   * confirmation mid-flow.
   */
  deletingCacheName: string;
  /**
   * Opens the DeleteDialog and snapshots the current active cache name. Use
   * this from the Delete button click handler instead of `setDeleteDialogOpen`
   * directly so the snapshot stays in sync with the open state.
   */
  openDeleteDialog: () => void;
  /** Active toast to render via createPortal (null = no toast). */
  activeToast: ToastPayload | null;
  handleSelectNoFilter: () => void;
  handleSelectCache: (cache: FormCacheDTO) => Promise<void>;
  handleUpsert: (name: string) => Promise<void>;
  handleUpdateLastInUse: (id: string) => Promise<void>;
  handleDelete: () => Promise<void>;
  handleRename: (newName: string) => Promise<void>;
  setActiveId: (id: string | null) => void;
}

export function useFormCacheSelector<TFilter>(
  cacheId: string,
  filter: TFilter,
  onSelected: FormCacheSelectorProps<TFilter>['onSelected'],
  httpClient: HttpClient,
): UseFormCacheSelectorResult<TFilter> {
  const { t } = useTranslation('form-cache-selector');

  // ── API client (memoized so mutations always use the same reference) ──────
  const api = useMemo(() => createFormCacheApi(httpClient), [httpClient]);

  // ── Local UI state ───────────────────────────────────────────────────────
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isMutating, setIsMutating] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveNameError, setSaveNameError] = useState<string | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingCacheName, setDeletingCacheName] = useState('');
  const [caches, setCaches] = useState<FormCacheDTO[]>([]);
  const [activeToast, setActiveToast] = useState<ToastPayload | null>(null);

  // ── Toast auto-dismiss ───────────────────────────────────────────────────
  // Clears the toast after 5 s. The cleanup cancels any pending timer so rapid
  // mutations don't stack overlapping dismissals.
  useEffect(() => {
    if (!activeToast) return;
    const id = setTimeout(() => setActiveToast(null), 5000);
    return () => clearTimeout(id);
  }, [activeToast]);

  // ── Live filter ref ──────────────────────────────────────────────────────
  // The `filter` prop is the live form state. We keep a ref so async mutation
  // handlers always read the latest value at the moment they execute, not the
  // value captured when the closure was created (R-003).
  const filterRef = useRef<TFilter>(filter);
  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  // ── Mount-time empty-filter snapshot ─────────────────────────────────────
  // The architecture references an `emptyTFilter` template for fallbacks. We
  // capture the prop value at mount time — a stable reference that the
  // consumer is expected to provide as a "zeroed" version of TFilter. The ref
  // guarantees subsequent live mutations of `filter` cannot corrupt the
  // fallback used by safeParse / onSelected.
  const emptyFilterRef = useRef<TFilter>(filter);

  // ── Exactly-once mount call guard ────────────────────────────────────────
  // Set to true the first time the auto-restore effect fires onSelected,
  // ensuring StrictMode / refetch / re-render cannot trigger a second call.
  const hasRestoredRef = useRef(false);

  // ── GetAllByUser ─────────────────────────────────────────────────────────
  const queryKey = useMemo(() => formCacheKeys.all(cacheId), [cacheId]);
  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery<FormCacheDTO[]>({
    queryKey,
    queryFn: () => api.getAllByUser(cacheId),
    enabled: cacheId.length > 0,
  });

  // Keep the local `caches` state in sync with the server data. Mutations
  // (upsert / delete / rename) work against this local copy so the UI can
  // update without forcing a full GetAllByUser refetch.
  useEffect(() => {
    if (data) setCaches(data);
  }, [data]);

  // ── Mount-time auto-restore effect ───────────────────────────────────────
  useEffect(() => {
    if (hasRestoredRef.current) return;

    // Loading: wait until the query resolves one way or the other.
    if (isLoading) return;

    if (isError) {
      hasRestoredRef.current = true;
      // NFR-FFCS-STB-002: surface via toast but never block parent form.
      const message = error instanceof Error ? error.message : 'unknown';
      console.warn('[FormCacheSelector] GetAllByUser failed:', message);
      setActiveToast({ text: message, color: 'red' });
      void onSelected(emptyFilterRef.current, false);
      return;
    }

    if (!data) return;

    const lastInUseItem = data.find((c) => c.lastInUse === true);
    hasRestoredRef.current = true;

    if (lastInUseItem) {
      // GetById → safeParse → onSelected(parsed, true) → setActiveId on accept.
      // The double cast through `unknown` is a deliberate, narrow widening:
      // safeParse is parametric over `Record<string, unknown>` (the JSON shape
      // it can introspect with `typeof`), but the consumer's TFilter is opaque
      // here. The cast is sound because:
      //   - `emptyFilterRef.current` IS the consumer's TFilter at runtime.
      //   - safeParse never adds keys not present in emptyFilter.
      //   - The returned object structurally matches TFilter.
      const emptyAsRecord = emptyFilterRef.current as unknown as Record<string, unknown>;

      api
        .getById(lastInUseItem.id)
        .then((rawJson) => safeParse(rawJson, emptyAsRecord))
        .then((restored) => onSelected(restored as unknown as TFilter, true))
        .then(() => {
          // R-001: always mark the lastInUse item as active regardless of
          // whether the consumer accepted the filter into its form state. The
          // component's selection state (dropdown highlight) reflects what the
          // server says was last used, independent of the consumer's veto.
          setActiveId(lastInUseItem.id);
        })
        .catch((err) => {
          console.warn(
            '[FormCacheSelector] GetById failed during auto-restore:',
            err,
          );
          void onSelected(emptyFilterRef.current, false);
        });
    } else {
      // No lastInUse → empty fallback, no GetById call.
      void onSelected(emptyFilterRef.current, false);
    }
    // We intentionally exclude `onSelected` from the dependency array because
    // the consumer is not required to wrap it in useCallback — and the
    // hasRestoredRef guard already prevents double fires.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isError, error]);

  // ── Mutation handlers — S012 implements select; S013-S015 the rest ────────
  /**
   * "Sin filtros" pseudo-option click (S012, AC-007).
   * - Clears `activeId` so the dropdown UI shows no Check.
   * - Calls `onSelected(emptyFilter, false)` so the parent form resets to its
   *   zeroed state.
   * - Does NOT call any backend API (no `lastInUse` row to clear server-side).
   */
  const handleSelectNoFilter = () => {
    setActiveId(null);
    void onSelected(emptyFilterRef.current, false);
  };

  /**
   * Named cache click (S012, AC-006).
   *
   * Pipeline:
   *   0. Optimistic: set `activeId = cache.id` immediately so the trigger
   *        reflects the click at once (R-001 — instant visual feedback).
   *   1. `api.getById(cache.id)` → raw JSON.
   *   2. `safeParse(rawJson, emptyFilter)` → typed TFilter (never throws).
   *   3. `onSelected(restored, true)` → consumer decides whether to accept.
   *   4. If consumer returns `true`:
   *        - fire-and-forget `api.updateLastInUse(cache.id)` (R-003)
   *          — UX §7.3 lastInUse update timing: backend write does NOT block
   *          the parent form update.
   *
   * Any failure in steps 1-3 falls through to the catch block, which:
   *   - Reverts `activeId` to `null` ("Sin filtros") — consistent with the
   *     `onSelected(emptyFilter, false)` call that follows (R-001: trigger
   *     must match form state).
   *   - Calls `onSelected(emptyFilter, false)` so the parent form is left in
   *     a known (empty) state rather than partially-restored garbage.
   *   - Surfaces the error via `setActiveToast`.
   */
  const handleSelectCache = async (cache: FormCacheDTO): Promise<void> => {
    // R-001: optimistic update — give instant visual feedback on click.
    // Reverted in the catch block if the pipeline fails.
    setActiveId(cache.id);
    try {
      const rawJson = await api.getById(cache.id);
      const emptyAsRecord = emptyFilterRef.current as unknown as Record<
        string,
        unknown
      >;
      const restored = safeParse(rawJson, emptyAsRecord) as unknown as TFilter;
      const shouldUpdate = await onSelected(restored, true);
      if (shouldUpdate) {
        // Fire-and-forget: do NOT await. Failure is non-fatal — the user still
        // got the restored filter; the only loss is "last used" stickiness on
        // their next visit.
        void api.updateLastInUse(cache.id).catch((err) => {
          console.warn(
            '[FormCacheSelector] updateLastInUse failed (non-fatal):',
            err,
          );
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'unknown';
      console.warn('[FormCacheSelector] handleSelectCache failed:', message);
      // R-001: revert trigger to "Sin filtros" — consistent with the
      // onSelected(emptyFilter, false) call below that resets the form.
      // Use functional update to avoid reverting a concurrent selection.
      setActiveId((prev) => (prev === cache.id ? null : prev));
      setActiveToast({ text: message, color: 'red' });
      void onSelected(emptyFilterRef.current, false);
    }
  };

  /**
   * Save / Upsert handler (S013, FR-FFCS-015 / FR-FFCS-016).
   *
   * Single entry point for both "save new" and "save overwrite":
   *   - Save new: parent calls with the dialog-supplied name; the API
   *     creates a fresh row and returns its id.
   *   - Save overwrite: parent calls with the active cache's existing name;
   *     the API recognises the name + cacheId + userId tuple and updates the
   *     existing row's filter JSON, returning the same id.
   *
   * Update strategy: pessimistic (R-002). We wait for the server-returned id
   * before touching local state because:
   *   - Save-new needs the id to set `activeId` correctly.
   *   - Optimistic insertion would require a synthetic id and roll-back logic
   *     for the rare 5xx case.
   *   - The latency on a write is small enough that the spinner UX is fine.
   *
   * Filter freshness: we read `filterRef.current` at call time — NOT a
   * stale closure capture from when `handleUpsert` was last memoised. This
   * matters when the user keeps editing the form while the dialog is open.
   */
  const handleUpsert = async (name: string): Promise<void> => {
    setIsMutating(true);
    setSaveNameError(null);
    try {
      const response = await api.upsert({
        cacheId,
        name,
        filter: filterRef.current as unknown,
      });

      // Update the local list pessimistically using the server-returned id.
      // - If the id already exists locally → it's an overwrite: update name +
      //   flip lastInUse to true on this row, false on every other row.
      // - Otherwise → it's a new row: append it and clear lastInUse on others.
      setCaches((prev) => {
        const existing = prev.find((c) => c.id === response.id);
        if (existing) {
          return prev.map((c) =>
            c.id === response.id
              ? { ...c, name, lastInUse: true }
              : { ...c, lastInUse: false },
          );
        }
        return [
          ...prev.map((c) => ({ ...c, lastInUse: false })),
          { id: response.id, name, lastInUse: true },
        ];
      });

      setActiveId(response.id);
      setSaveDialogOpen(false);
      setActiveToast({ text: t('messages.saved'), color: 'green' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'unknown';
      // 409 → inline conflict in the dialog. Anything else → toast error.
      if (message.includes('409')) {
        setSaveNameError(NAME_CONFLICT_KEY);
      } else {
        setActiveToast({ text: message, color: 'red' });
      }
    } finally {
      setIsMutating(false);
    }
  };

  const handleUpdateLastInUse = async (_id: string): Promise<void> => {
    /* implemented in S013 — covered by handleSelectCache fire-and-forget */
  };
  /**
   * Delete handler (S015, FR-FFCS-018).
   *
   * Pipeline:
   *   1. Guard: a delete without an active id is a no-op (the button is
   *      hidden in that state, but the guard is defensive — Delete is only
   *      offered when a named cache is selected).
   *   2. `api.delete(activeId)` — backend returns true on 200.
   *   3. On 200: remove the entry from the local list. R-002: the local
   *      state updates (caches filter, dialog close, activeId = null) MUST
   *      complete BEFORE `onSelected(emptyFilter, false)` is called so the
   *      parent form sees a consistent component state when its onSelected
   *      callback fires.
   *   4. On 404 or generic error: close the dialog and surface the message
   *      via the global error banner. 404 here means the row was already
   *      deleted out from under us by another tab — there is nothing to
   *      retry, so closing is the correct UX.
   */
  const handleDelete = async (): Promise<void> => {
    if (!activeId) return;
    const deletedId = activeId;
    setIsMutating(true);
    try {
      await api.delete(deletedId);
      // R-002: local state updates BEFORE the onSelected callback fires.
      setCaches((prev) => prev.filter((c) => c.id !== deletedId));
      setDeleteDialogOpen(false);
      setActiveId(null);
      setActiveToast({ text: t('messages.deleted'), color: 'green' });
      void onSelected(emptyFilterRef.current, false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'unknown';
      setDeleteDialogOpen(false);
      setActiveToast({ text: message, color: 'red' });
    } finally {
      setIsMutating(false);
    }
  };

  /**
   * Opens the DeleteDialog and snapshots the current active cache's name
   * (R-001). The snapshot is read by the dialog's confirmation text so a
   * concurrent rename mid-confirmation cannot change the displayed name.
   */
  const openDeleteDialog = useCallback(() => {
    const name =
      activeId !== null
        ? (caches.find((c) => c.id === activeId)?.name ?? '')
        : '';
    setDeletingCacheName(name);
    setDeleteDialogOpen(true);
  }, [activeId, caches]);

  /**
   * Rename handler (S014, FR-FFCS-017).
   *
   * Pipeline:
   *   1. Guard: a rename without an active id is a no-op (the button is hidden
   *      in that state, but the guard is defensive).
   *   2. `api.rename(activeId, newName)` — backend returns true on 200.
   *   3. On 200: in-place list update — same `id`, new `name`, `lastInUse`
   *      untouched. Dialog closes. The selector trigger label re-renders with
   *      the new name automatically because it reads `caches.find(...).name`.
   *   4. On 409 (FFCS-100): `renameError` is set; dialog stays open so the
   *      user can fix the name and retry. The global banner is left clear.
   *   5. On 404 / other errors: dialog closes, global banner shows the message.
   *      404 specifically means the row was deleted out from under us by
   *      another tab — there is nothing to retry, so closing is the correct UX.
   */
  const handleRename = async (newName: string): Promise<void> => {
    if (!activeId) return;
    setIsMutating(true);
    setRenameError(null);
    try {
      await api.rename(activeId, newName);
      setCaches((prev) =>
        prev.map((c) => (c.id === activeId ? { ...c, name: newName } : c)),
      );
      setRenameDialogOpen(false);
      setActiveToast({ text: t('messages.renamed'), color: 'green' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'unknown';
      if (message.includes('409')) {
        // Inline conflict — keep dialog open.
        setRenameError(NAME_CONFLICT_KEY);
      } else {
        // 404 or generic — close dialog + toast error.
        setRenameDialogOpen(false);
        setActiveToast({ text: message, color: 'red' });
      }
    } finally {
      setIsMutating(false);
    }
  };

  const clearSaveNameError = useCallback(() => {
    setSaveNameError(null);
  }, []);

  const clearRenameError = useCallback(() => {
    setRenameError(null);
  }, []);

  return {
    caches,
    activeId,
    isLoading,
    isMutating,
    saveDialogOpen,
    setSaveDialogOpen,
    saveNameError,
    clearSaveNameError,
    renameDialogOpen,
    setRenameDialogOpen,
    renameError,
    clearRenameError,
    deleteDialogOpen,
    setDeleteDialogOpen,
    deletingCacheName,
    openDeleteDialog,
    activeToast,
    handleSelectNoFilter,
    handleSelectCache,
    handleUpsert,
    handleUpdateLastInUse,
    handleDelete,
    handleRename,
    setActiveId,
  };
}
